import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  generateRandomId,
  isMeetingSessionRoleGreaterThanOrEqual,
  isMeetingSessionRoleLessThan,
  isSpectator,
  MeetingSessionOvenMediaReceiver,
  useMeetingAudioMixer
} from "@kalyzee/kast-web-sdk";
import {
  addComponentOnOverlay,
  audioContextIsReadyToUse,
  awaitWithMinimumDelay,
  Loading,
  MediaCaptureRef,
  removeComponentOnOverlay,
  useRefAutoUpdate,
  useStateWithRef
} from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, MeetingRoomStatus, MeetingSessionAddMedia, MeetingSessionHighlightingState, MeetingSessionRole, MeetingSessionStatus, MeetingState, MeetingUpdateByIdResult, SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../../helpers/toast";
import { MeetingSourceExtended, useMeetingConfigurator, useMeetingDataOnChange, useMeetingSelector, useMeetingSources, useMultiMeetingAPI } from "../../../../hooks/meeting";

import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { isExpertVideoSession } from "../../../../helpers/meeting";

import PauseView from "../../../../components/utils/PauseView";
import { useSettings } from "../../../../hooks/settings";

import { BottomPanelWebrtcSessionStats } from "../../../../components/debug/BottonPanelWebrtcSessionStats";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import OverlayInformationMessage from "../../../../components/utils/OverlayInformationMessage";
import { useActivityData, useBroadcastingExpertSessions, usePopulatedMeetingData, useStudentAvailableViews } from "../../../../hooks/populatedData";

import { BoxOverlay, BoxOverlayProps, CircleIcon, OverlayContainer, ReactIcons, SecondarySideBar, SquareIcon, TopBar } from "@kalyzee/kalyzee-ui";
import { MeetingContainer, MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { MediaSourceViewer } from "../../../../components/common/MediaSourceViewer";
import { BoardInputsWorkBox } from "../../../../components/workBoxes/BoardInputsWorkBox";
import { ChatWorkBox } from "../../../../components/workBoxes/ChatWorkBox";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { addPopup, removePopup, removePopupByGroup } from "../../../../helpers/popup";
import { isExpert } from "../../../../helpers/roles";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { expertWebcamName } from "../../../../helpers/webcam";

import clsx from "clsx";
import Chat, { DashboardChatRef } from "../../../../components/chat/Chat";
import { AppSoundSlider } from "../../../../components/utils/AppSoundSlider";
import { useAppStoreDispatch } from "../../../../contexts/appStore/context";
import { useBoardMedia } from "../../../../hooks/board";
import styles from "./meeting.module.css";
import { useMeetingMasterMessagingBridge } from "../../../../hooks/secondaryScreens/messagingBridge";import { LayoutType } from "../../../../interfaces/ui";
import { useAppContext } from "../../../../contexts/app/context";
import { LoadingView } from "../../../../components/utils/LoadingView";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { SurveyWorkBox } from "../../../../components/workBoxes/survey/SurveyWorkbox";
import { MainClockElapsedRenderer } from "../../../../components/renderers/mainClock";
;

export const MeetingStudentPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const highlightMode = useRef(false);
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const selfUser = useAppStoreSelector(state => state.user);
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);
  const outputAudioDeviceInfo = useAppStoreSelector(state => state.outputAudioDeviceInfo);
  const layout = useAppStoreSelector(state => state.layout);

  const dispatch = useAppStoreDispatch();

  const [overlayMode, setOverlayMode, overlayModeRef] = useStateWithRef<"none" | "media" | "screen">("none");

  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  // If a Peda Meeting API is available, we use it and it becomes the focusedMeetingAPI
  // Otherwise, we use the URL selected meeting API
  const { selectedMeetingAPI, pedagogy, focusedMeetingAPI } = useMultiMeetingAPI(
    pageContextRef.current?.params?.id,
    true,
    true // only selected if no other running
  );
  const focusedMeetingAPIRef = useRefAutoUpdate(focusedMeetingAPI);

  const [showEndMeetingConfirmationPopup, setShowEndMeetingConfirmationPopup] = useState(false);

  const autojoin = pageContextRef.current?.searchParams?.get("autojoin") === "true";

  (window as any).focusedMeetingAPI = focusedMeetingAPI;
  useMeetingConfigurator(focusedMeetingAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  const [meeting] = useMeetingSelector(focusedMeetingAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(focusedMeetingAPI, (state) => state.session);
  const [rooms] = useMeetingSelector(focusedMeetingAPI, (state) => state.rooms);
  const [emissionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.emissionSessions);
  const [receptionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.receptionSessions);
  const [survey] = useMeetingSelector(focusedMeetingAPI, (state) => state.surveys?.[0]);

  const [selectedSession] = useMeetingSelector(selectedMeetingAPI, (state) => state.session);

  const mediaSelectorRef = useRef<MediaCaptureRef>();
 
  const chatRef = useRef<DashboardChatRef>(null);

  const [audioPermitted] = useMeetingSelector(focusedMeetingAPI, (state) => state.session?.permissions?.audio ?? true);

  const [workBoxMode, setWorkBoxMode] = useState<"none" | "inputs" | "requests" | "chat" | "survey">("none");

  const [chatAdapters] = useMeetingSelector(focusedMeetingAPI, (state) => state.chatAdapters);
  const chat = useMemo(() => {
    const room = meeting?.chatrooms?.[0];
    if (!room) return undefined;
    const adapter = chatAdapters?.[room.id];
    return { room, adapter };
  }, [meeting, chatAdapters]);
  const [unreadMessagesNb, setUnreadMessagesNb] = useState(0);

  const isStoppingRef = useRef(false);

  const sources = useMeetingSources();

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    return isSpectator(session?.role) ? [] : ((sources ?? []) as any);
  }, [sources, session?.role]);

  const boardLocalOrRemoteMedia = useBoardMedia(focusedMeetingAPI);
  
  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));
    
    return { enabledMedia };
  }, [meeting, boardLocalOrRemoteMedia, meetingOptions]);

  const surveyWorkBoxCanBeOpened = (
    (session?.role && isMeetingSessionRoleGreaterThanOrEqual(session.role, MeetingSessionRole.MODERATOR))
    || survey
  );

  useMeetingMasterMessagingBridge();

  useEffect(() => {
    const handle = chat?.adapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chat?.adapter]);

  // (window as any).studentCtx = studentCtx;

  useMeetingDataOnChange(
    focusedMeetingAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  useEffect(() => {
    const emissionManager = focusedMeetingAPI?.emissionManager;
    if (emissionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (emissionManager.paused) emissionManager.restore();
      } else {
        if (!emissionManager.paused) emissionManager.pause();
      }
    }
    const receptionManager = focusedMeetingAPI?.receptionManager;
    if (receptionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (receptionManager.paused) receptionManager.restore();
      } else {
        if (!receptionManager.paused) receptionManager.pause(); 
      }
    }
  }, [focusedMeetingAPI, meeting?.state])

  /**
   * Switching between paused and running when a pedagogy is available for us
   */
  useEffect(() => {
    if (!selectedSession) return;

    const status = selectedSession?.status;

    const waitedInPedagogy = !!pedagogy?.myRoom;
    if (waitedInPedagogy && status === MeetingSessionStatus.RUNNING) {
      // A peda meeting is available but we need to pause this meeting in
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.PAUSED,
      });
    }

    if (!waitedInPedagogy && status === MeetingSessionStatus.PAUSED) {
      // No peda meeting available, we can unpause the current meeting
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.RUNNING,
      });
    }
  }, [pedagogy, selectedMeetingAPI, selectedSession]);

  /**
   * If we are connected to a pedagogy meetign but we no longer
   * have a room waiting for us in it, we need to leave it
   */
  useEffect(() => {
    if (pedagogy && !pedagogy.myRoom) {
      pedagogy.container.getAPI().leave();
    }
  }, [pedagogy]);

  ////////////////////// Populated DATA //////////////////////

  const populatedMeetingData = usePopulatedMeetingData(focusedMeetingAPI);
  const activityData = useActivityData(populatedMeetingData);

  const availableViews = useStudentAvailableViews(populatedMeetingData, activityData, selfUser?.id, session?.id);

  const currentView = useMemo(() => {
    if (pageContext?.page === PageMasterMode.MEETING_HIGHLIGHT) {
      const highlightView = availableViews.find((view) => view.type === "highlight");
      if (highlightView) return highlightView;
    }
    return availableViews[availableViews.length - 1]; // TODO : there should be a default view, like this structure : { availableViews, defaultView, currentView }
  }, [availableViews, pageContext]);

  const broadcastingExpertSessions = useBroadcastingExpertSessions(populatedMeetingData);

  const expertNonWebcamVideoSessions = useMemo(
    () =>
      (currentView?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter((videoSession) => videoSession.data.mediaSession.name !== expertWebcamName),
    [currentView]
  );

  ////////////////////// Work Boxes DATA //////////////////////

  useEffect(() => {
    if (!session || !focusedMeetingAPIRef.current) return;
    const highlightView = availableViews.find((view) => view.type === "highlight");
    if (!highlightView) return;

    // My session is highlighted and confirmed (video:true)
    const mySessionData = highlightView.students.find((s) => s.video && s.session.id === session.id);

    if (mySessionData && !focusedMeetingAPIRef.current?.emissionManager.audioIsEnabled("cameras")) {
      focusedMeetingAPIRef.current?.unmute({ direction: "outgoing", group: "cameras" });
    }
  }, [session, availableViews])

  const canHearSession = (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
    return true; // everyone hears everyone, no group mode for now
  };

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(), 
    focusedMeetingAPI, 
    true, 
    canHearSession, 
    { ...(settings.debug?.audio?.mixer ?? {}), forceEnableDecoderHack: true }
  );

  const myGroupStatus = populatedMeetingData?.me.group?.room.status;
  const canShareScreen = session?.highlighting === MeetingSessionHighlightingState.ONGOING || (myGroupStatus && myGroupStatus !== MeetingRoomStatus.Closed);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  /**
   * Joins a container, not necessarily the current one
   */
  const joinContainer = async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;
    const containerAPI = container.getAPI();
    //if (studentCtx) {
      if (audioEnabled) await containerAPI.unmute({ direction: "outgoing", group: "cameras" });
      else await containerAPI.mute({ direction: "outgoing", group: "cameras" });
      if (videoEnabled) await containerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      else await containerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      dispatch("setAudioStreamEnabled", containerAPI.emissionManager.audioIsEnabled("cameras"));
      dispatch("setVideoStreamEnabled", containerAPI.emissionManager.videoIsEnabled("cameras"));
   // }

    const medias: MeetingSessionAddMedia<any, any, any>[] = (sources as MeetingSourceExtended[]).map((s) => {
      const type = s.extra.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      return {
        video: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.videoIsEnabled(group),
        },
        audio: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.audioIsEnabled(group),
        },
        name: s.name,
        deviceId: s.id,
        extra: {
          type,
          label: s.name,
          controls: s.controls?.type,
        },
      };
    });

    const result = await containerAPI.join({
      medias,
    });

    if (result.error) {
      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
  };

  const createAndJoinContainer = async (id: string) => {
    const api = multiMeetingAPIRef.current;

    if (!api.all) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }

    const container = api.all.getOrCreateContainer(id);
    joinContainer(container);
  };

  const leave = async () => {
    // leave all
    const promises: Promise<any>[] = [];
    multiMeetingAPIRef.current?.all?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  const updateMedias = useCallback(async () => {
    const meetingContainerAPI = focusedMeetingAPIRef.current;
    if (!meetingContainerAPI) return;
    const manager = meetingContainerAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let camerasAudioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasAudioIsEnabled = camerasAudioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!camerasAudioIsEnabled && manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.mute({ direction: "outgoing", group: "cameras" });
      } else if (camerasAudioIsEnabled && !manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.unmute({ direction: "outgoing", group: "cameras" });
      }
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    dispatch("setAudioStreamEnabled", manager.audioIsEnabled("cameras"));
    dispatch("setVideoStreamEnabled", manager.videoIsEnabled("cameras"));
  }, [dispatch, session, meeting]);

  const onLayoutChange = (type: LayoutType) => {
    dispatch("setLayout", type);
  };

  // ------------------ EXPERT UTILS ------------------ //

  const updateMeetingState = async (state: MeetingState) => {
    pageContextRef.current?.setLoading(true);
    let result: MeetingUpdateByIdResult | undefined;
    try {
      result = await focusedMeetingAPIRef.current?.updateMeetingState(state);
    } catch (err) {
    } finally {
      pageContextRef.current?.setLoading(false);
    }
    return result;
  };

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const stop = async (): Promise<boolean> => {
    // pageContextRef.current?.setLoading(true);
    // await delay(200);
    setShowEndMeetingConfirmationPopup(true);
    //const result = await updateMeetingState(MeetingState.STOPPED);
    // pageContextRef.current?.setLoading(false);
    // if (result && !result.error && result.response?.meeting?.state === MeetingState.STOPPED) {
    //   pageContextRef.current?.goTo(PageMasterMode.LIST);
    //   return true;
    // }
    // toastError("Une erreur est apparue.");
    return false;
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (workBoxMode === "survey" && !surveyWorkBoxCanBeOpened) {
      setWorkBoxMode("none");
    }
  }, [surveyWorkBoxCanBeOpened, workBoxMode]);

  useEffect(() => {
    const containers = multiMeetingAPIRef.current.all?.getContainers();
    containers?.forEach((container) => {
      joinContainer(container);
    });
  }, [multiMeetingAPI.all]);

  useEffect(() => {
    // Not ready to use, we cannot decide if a redirection is needed
    if (!meeting) return;

    // I am in a pedagogy meeting
    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      // I keep the ID of my current meeting, this will be used as an origin meeting
      // => to know what meeting i'm coming from
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    // Priority to highllighted content
    if (availableViews.find((view) => view.type === "highlight")) {
      if (highlightMode.current) {
        // We already had highlighted sessions (or were highlighted ourself) before this update
        // Maybe we are on the hightlight page, maybe not, does not matter
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
      highlightMode.current = true;
      return;
    }
    highlightMode.current = false;

    // Nothing is highlighted, maybe we have active groups
    // if (haveActiveGroups) {
    //   if (pageContextRef.current?.page === PageMasterMode.MEETING_GROUP) {
    //     return;
    //   }
    //   pageContextRef.current?.goTo(PageMasterMode.MEETING_GROUP, { id: pageContextRef.current?.params?.id });
    //   return;
    // }

    // Nothing is highlighted, nothing is active, we can go back to the dashboard
    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [availableViews, pageContextRef, populatedMeetingData]);

  useEffect(() => {
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(audioEnabled);
    mediaSelectorRef.current.enableVideo(videoEnabled);
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    if (outputAudioDeviceInfo && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(outputAudioDeviceInfo).catch(() => {
        console.error(`audio device (${JSON.stringify(outputAudioDeviceInfo)}) is invalid`);
      });
    }
  }, [audioMixer, outputAudioDeviceInfo]);

  useEffect(() => {
    const id = pageContextRef.current?.params?.id ?? "";
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        createAndJoinContainer(id);
      }
    });
    return () => {
      leave();
    };
  }, []);

  // useEffect(() => {
  //   let currentPopup: React.ReactNode;
  //   const showMeetingNotReadyPopup = () => {
  //     if (currentPopup) {
  //       removeComponentOnOverlay(currentPopup);
  //       currentPopup = undefined;
  //     }
  //     currentPopup = (
  //       <Popup
  //         Header={<HeaderPopup icon={Icons.TimerIcon} title="Le cours va bientôt commencer, veuillez patienter" />}
  //         Content={<Loading style={{ width: "100%", "--loading-svg-color": "black" } as any} />}
  //         style={{
  //           width: "fit-content",
  //           height: "fit-content",
  //           padding: "2em",
  //         }}
  //       />
  //     );
  //     addComponentOnOverlay(currentPopup, `meeting_state_not_ready_${generateRandomId()}`);
  //   };

  //   const state = meeting?.state;
  //   if (state === MeetingState.IDLE) {
  //     showMeetingNotReadyPopup();
  //   } else {
  //     if (currentPopup) {
  //       removeComponentOnOverlay(currentPopup);
  //       currentPopup = undefined;
  //     }
  //   }

  //   if (state === MeetingState.IDLE) showMeetingNotReadyPopup();

  //   return () => {
  //     if (currentPopup) {
  //       removeComponentOnOverlay(currentPopup);
  //       currentPopup = undefined;
  //     }
  //   };
  // }, [meeting?.state]);

  useEffect(() => {
    emissionSessions?.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group) s.data.groups = [group];
    });
  }, [emissionSessions]);

  useEffect(() => {
    receptionSessions?.forEach((s) => {
      s.data.extra = {
        video: isExpertVideoSession(meeting, s),
        audio: true,
      };
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = focusedMeetingAPI;
  }, [settings, audioMixer, focusedMeetingAPI]);

  // ------------------ EXPERT EFFECTS ------------------ //

  useEffect(() => {
    const state = meeting?.state;

    let currentPopup: React.ReactNode;
    const endMeetingPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      const stopped = state === MeetingState.STOPPED;

      let buttons: BoxOverlayProps["buttons"];
      
      if (stopped) {
        buttons = [
          {
            label: "Revenir à la liste des cours",
            onClick: () => {
              removePopup(currentPopup);
              removePopupByGroup("main");
              pageContextRef.current?.goTo(PageMasterMode.LIST);
            },
            validateAfter: autojoin ? 10 : undefined,
          }
        ];
        if (isExpert(session)) {
          buttons.push({
            label: "Relancer le cours",
            onClick: async () => {
              await start();
              removePopup(currentPopup);
            }
          });
        }
      } else {
        buttons = [
          {
            label: "Terminer le cours",
            onClick: async () => {
              await updateMeetingState(MeetingState.STOPPED);
            }
          },
          {
            label: "Poursuivre le cours",
            onClick: () => {
              setShowEndMeetingConfirmationPopup(false);
            }
          }
        ]
      }

      currentPopup = (
        <OverlayContainer
          style={{
            width: "100vw",
            height: "100vh",
          }}
          overlay={
            <BoxOverlay
              title={stopped ? "Le cours est terminé" : "Voulez-vous vraiment terminer le cours ?"}
              level={stopped ? "success" : "question"}
              buttons={buttons}
            />
          }
        />
      );
      
      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.STOPPED) {
      setShowEndMeetingConfirmationPopup(false);
    }

    if (state === MeetingState.IDLE) {
      start();
    } else if (state === MeetingState.STOPPED || showEndMeetingConfirmationPopup) {
      endMeetingPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state, showEndMeetingConfirmationPopup]);

  if (!meeting || !session || !rooms || !selfUser || !focusedMeetingAPI) return <LoadingView fullscreen message="Chargement en cours" />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderChatWorkBox = () => {
    if (!chat) return null;
    return (
      <ChatWorkBox 
        onClearMessages={isExpert(session) ? () => chatRef.current?.clearMessages() : undefined}
      >
        <Chat 
          meetingAPI={focusedMeetingAPI}
          chatroom={chat.room}
          chatAdapter={chat.adapter}
          ref={chatRef}
        />
      </ChatWorkBox>
    );
  };

  const renderWorkBox = () => {
    if (workBoxMode === "none") return null;
    if (workBoxMode  === "inputs") {
      return (
        <div style={{
          flex: 1,
        }}>
          <BoardInputsWorkBox 
            meetingAPI={focusedMeetingAPI}
          />
        </div>
      );
    }
    if (workBoxMode  === "chat") {
      return (
        <div style={{
          flex: 1,
        }}>
          {renderChatWorkBox()}
        </div>
      );
    }
    if (workBoxMode === "survey") {
      return (
        <div style={{
          flex: 2,
          minWidth: 2,
        }}>
          <SurveyWorkBox meetingAPI={focusedMeetingAPI} />
        </div>
      );
    }
  }
  
  const renderOverlay = () => {
    return (
      <MediaSourcePicker
        meetingAPI={focusedMeetingAPI}
        sources={usableSources} // all sources together, cameras and screens
        display
        className={clsx({
          [styles.mediaSourcePicker]: true,
          [styles.invisible]: overlayMode !== "media",
        })}
      />
    )
  }

  const renderDevSoundIndicator = () => (
    <div style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
      <DevSoundIndicatorsBanner 
        meetingData={populatedMeetingData} 
        canHearSession={(session) => canHearSession(focusedMeetingAPI, session)} 
      />
    </div>
  );

  const renderSideBar = () => {
    const top: React.ReactNode[] = [
      <SquareIcon.Variants.ComputerScreen 
        label={"Affichage"}
        selected={workBoxMode === "inputs"}
        onClick={() => setWorkBoxMode((v) => (v === "inputs" ? "none" : "inputs"))}
      />,
    ];
    const bottom: React.ReactNode[] = [];
    if (settings.experimental?.survey) {
      bottom.push(
        <SquareIcon.Variants.Poll 
          label="Sondage"
          selected={workBoxMode === "survey"}
          onClick={() => setWorkBoxMode((v) => (v === "survey" ? "none" : "survey"))}
          nativeProps={{
            disabled: !survey && isMeetingSessionRoleLessThan(session.role, MeetingSessionRole.MODERATOR),
          }}
          showPlus={
            !survey 
            && isMeetingSessionRoleGreaterThanOrEqual(session.role, MeetingSessionRole.MODERATOR)
            && workBoxMode !== "survey"
          }
          notificationCount={survey && workBoxMode !== "survey" ? 1 : undefined}
        />
      );
    }
    bottom.push(
      <SquareIcon.Variants.Chat 
        label="Chat"
        selected={workBoxMode === "chat"}
        onClick={() => setWorkBoxMode((v) => (v === "chat" ? "none" : "chat"))}
        notificationCount={unreadMessagesNb ? unreadMessagesNb : undefined}
      />
    );
    return (
      <SecondarySideBar 
        top={top}
        bottom={bottom}
      />
    );
  };

  const renderSurveyBar = () => {
    if (workBoxMode === "survey") return null;
    if (!survey || survey.status !== SurveyStatus.Open) return null;
    const question = survey.questions?.[0];
    if (!question) return null;
    return (
      <div 
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "1em",
          backgroundColor: "var(--color-element-survey-bar-bg)",
          color: "white",
          padding: "0.5em",
          cursor: "pointer",
        }}
        onClick={() => setWorkBoxMode("survey")}
      >
        <ReactIcons.Stats style={{ height: "1em", width: "1em" }} />
        {question.content}
        <MainClockElapsedRenderer
          since={survey.createdAt}
          render={elapsed => (
            <div style={{ opacity: 0.75, display: "flex", gap: "0.25em", fontSize: "1em", alignItems: "center" }}>
              <ReactIcons.Timer style={{ height: "1em" }} />
              {elapsed}
            </div>
          )}
        />
      </div>
    );
  }
 
  const renderBottomBar = () => {
    const left: React.ReactNode[] = [];
    
    if (enabledMedia.length > 1) {
      left.push(
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5em",
          alignItems: "center",
          height: "100%",
        }}>
          <SquareIcon.Variants.HorizontalLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.HorizontalSquare}
            onClick={() => onLayoutChange(LayoutType.HorizontalSquare)}
          />
          <SquareIcon.Variants.VerticalLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.VerticalSquare}
            onClick={() => onLayoutChange(LayoutType.VerticalSquare)}
          />
          <SquareIcon.Variants.GridLayout 
            containerSize={50}
            iconSize={25}
            selected={layout === LayoutType.Square}
            onClick={() => onLayoutChange(LayoutType.Square)}
          />
        </div>
      );
    }

    const center: React.ReactNode[] = [];
    const renderCameraButton = () => {
      const permitted = !(session.permissions?.video === false);
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
      return (
        <div ref={currRef} key="student-dashboard-overlay-camera-btn">
          <CircleIcon.Variants.Camera
            unavailable={!permitted}
            disabled={!videoEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.videoIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.disableVideo({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.enableVideo({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              dispatch("setVideoStreamEnabled", manager.videoIsEnabled("cameras"));
            }}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    const renderAudioButton = () => {
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
      return (
        <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
          <CircleIcon.Variants.Mic
            unavailable={!audioPermitted}
            disabled={!audioEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.audioIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.mute({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.unmute({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              dispatch("setAudioStreamEnabled", manager.audioIsEnabled("cameras"));
            }}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    
    const renderPauseButton = () => {
      return (
        <CircleIcon.Variants.Pause
          onClick={() => updateMeetingState(MeetingState.PAUSED)}
        />
      );
    };

    const renderHangOffButton = () => {
      return (
        <CircleIcon.Variants.HangOff
          onClick={stop}
          style={{
            padding: "0.75em",
          }}
        />
      );
    };

    if (!isSpectator(session)) {
      if (isExpert(session)) {
        center.push(renderPauseButton());
        center.push(renderHangOffButton());
      }

      center.push(renderCameraButton());
      center.push(renderAudioButton());
    }

    const right: React.ReactNode[] = [
      <AppSoundSlider />,
      <MediaSourceViewer 
        sources={usableSources}
        meetingAPI={focusedMeetingAPI}
        onClick={() => setOverlayMode((v) => (v === "media" ? "none" : "media"))}
        selected={overlayMode === "media"}
      />, // shows local sources, screen / cameras
    ];
    
    return (
      <TopBar 
        style={{
          height: "unset",
          padding: "unset",
          backgroundColor: "transparent",
          boxShadow: "unset",
        }}
        left={left}
        center={center}
        right={right}
      />
    );
  }

  const renderUI = (children: React.ReactNode) => {
    return (
      <div style={{
        width: "100%",
        height: "100%",
        display: "flex",
      }}>
        {renderSideBar()}
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
          {renderSurveyBar()}
          <div style={{
            height: "100%",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: "1em",
            gap: "1em",
          }}>
            <div style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
            }}>
              <div style={{
                display: "flex",
                height: "100%",
                gap: "1em",
              }}>
                {renderWorkBox()}
                <div style={{
                  flex: 2,
                  position: "relative",
                  minWidth: 0,
                }}>
                  {children}
                  {renderOverlay()}
                  {renderDevSoundIndicator()}
                </div>
              </div>
            </div>
            {renderBottomBar()}
          </div>
        </div>
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <MasterUI>
      <PauseView
        meeting={meeting}
        onResume={() => updateMeetingState(MeetingState.RUNNING)}
        enabled={isExpert(session)}
      >
        <div className={styles.mainContainer}>
          {isSpectator(session) ? <div className={styles.spectatorBar}>Spectateur</div> : null}
          <div className={styles.content}>
            {renderUI(<Outlet />)}
          </div>
          {renderDebugOutgoingStream()}
        </div>
      </PauseView>
    </MasterUI>
  );
};
